import { ChevronDownIcon, PlusIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { useState } from 'react'

import { Button, Text } from '../../common/designs'
import type { InheritanceStepCriteria } from '../Processes/types'
import StepMatchingCriteriaForm from './StepMatchingCriteriaForm'
import {
  PROCESS_MATCHING_CRITERIA_MAX_STEPS_DEFAULT,
  PROCESS_MATCHING_CRITERIA_MAX_TIME_SEC_DEFAULT,
} from './constants'
import type { StepCriteriaRow } from './types'

interface Props {
  title: string
  description: string
  /** Stable prefix for rule row React keys (must not depend on editable fields or rows remount on Apply). */
  rowKeyPrefix: string
  currentStepCriteriaArray: StepCriteriaRow[]
  onUpdateStepCriteriaArray: (updatedArray: StepCriteriaRow[]) => void
  isDistanceMetricsEnabled?: boolean
  /** When true, this section starts expanded; each section toggles independently */
  isDefaultExpanded?: boolean
  onRegisterNewRowNeedsApply?: (rowClientId: string) => void
  onRowRuleApplied?: (rowClientId: string) => void
  onRowRuleRemoved?: (rowClientId: string) => void
}

const StepMatchingCriteriaSection = ({
  title,
  description,
  rowKeyPrefix,
  currentStepCriteriaArray,
  onUpdateStepCriteriaArray,
  isDistanceMetricsEnabled = false,
  isDefaultExpanded = false,
  onRegisterNewRowNeedsApply,
  onRowRuleApplied,
  onRowRuleRemoved,
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(isDefaultExpanded)

  const onSaveStepCriteria = (formData: InheritanceStepCriteria, dataIndex: number) => {
    const rowClientId = currentStepCriteriaArray[dataIndex]?.rowClientId
    const updatedCriteriaArray = [...currentStepCriteriaArray]
    updatedCriteriaArray[dataIndex] = {
      ...formData,
      rowClientId: rowClientId ?? crypto.randomUUID(),
    }
    onUpdateStepCriteriaArray(updatedCriteriaArray)
  }

  const onDeleteStepCriteria = (dataIndex: number) => {
    const removed = currentStepCriteriaArray[dataIndex]
    if (removed) onRowRuleRemoved?.(removed.rowClientId)
    onUpdateStepCriteriaArray([
      ...currentStepCriteriaArray.slice(0, dataIndex),
      ...currentStepCriteriaArray.slice(dataIndex + 1),
    ])
  }

  const onAddNewCriteria = () => {
    setIsOpen(true)
    const rowClientId = crypto.randomUUID()
    const newCriteria: StepCriteriaRow = {
      app_name: '',
      window_name: '',
      max_step_distance: PROCESS_MATCHING_CRITERIA_MAX_STEPS_DEFAULT,
      max_time_sec_distance: PROCESS_MATCHING_CRITERIA_MAX_TIME_SEC_DEFAULT,
      rowClientId,
    }
    onRegisterNewRowNeedsApply?.(rowClientId)
    onUpdateStepCriteriaArray([...currentStepCriteriaArray, newCriteria])
  }

  const ruleCount = currentStepCriteriaArray.length

  return (
    <section
      className='rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50/90 to-white shadow-sm ring-1 ring-black/[0.03]'
      data-rule-list-prefix={rowKeyPrefix}
    >
      <div className='flex items-stretch gap-2 border-b border-gray-200/80 p-2 sm:px-3 sm:py-2.5'>
        <button
          type='button'
          className='flex min-w-0 flex-1 items-center gap-2 rounded-md py-0.5 text-left transition-colors hover:bg-gray-100/60'
          onClick={() => setIsOpen((o) => !o)}
        >
          <ChevronDownIcon
            className={clsx(
              'size-5 shrink-0 text-gray-500 transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
          <div className='min-w-0 flex-1'>
            <Text size='s' weight={600} color='darkGray' className='leading-snug'>
              {title}
            </Text>
            <Text size='xs' color='lightGray' className='mt-0.5'>
              {ruleCount} {ruleCount === 1 ? 'rule' : 'rules'}
            </Text>
          </div>
        </button>
        <Button
          size='xs'
          variant='white'
          className='shrink-0 self-center'
          iconStart={<PlusIcon className='size-4' />}
          onClick={() => onAddNewCriteria()}
        >
          Add rule
        </Button>
      </div>

      <div className={clsx(!isOpen && 'hidden')}>
        <div className='px-3 pb-3 pt-2'>
          {description ? (
            <Text size='xs' color='lighterGray' className='mb-2'>
              {description}
            </Text>
          ) : null}

          {currentStepCriteriaArray.length === 0 ? (
            <Text size='xs' color='lightGray' className='mb-2 italic'>
              No rules yet. Use Add rule in the section header above.
            </Text>
          ) : null}

          <div className='flex flex-col gap-2'>
            {currentStepCriteriaArray.map((stepCriteria, i) => (
              <StepMatchingCriteriaForm
                key={`${rowKeyPrefix}-${i}`}
                formIndex={i + 1}
                formData={stepCriteria}
                onSave={(formData: InheritanceStepCriteria) => onSaveStepCriteria(formData, i)}
                onDelete={() => onDeleteStepCriteria(i)}
                isDistanceMetricsEnabled={isDistanceMetricsEnabled}
                onAppliedSuccess={() => onRowRuleApplied?.(stepCriteria.rowClientId)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default StepMatchingCriteriaSection

